import {
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Link,
  List,
  ListIcon,
  ListItem,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import TitleWithLogo from '../../components/TitleWithLogo'
import { CheckIcon } from '@chakra-ui/icons'
import useApi from '../../common/utils/api/useApi'
import { BillErrorCode, BillingClient } from './BillingSlice'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { RootState } from '../../app/store'
import { useGAContextData } from '../../utils/google-analytics/useGAContextData'
import GoogleAnalyticsTagManager from '../../utils/google-analytics/GoogleAnalyticsTagManager'

const standardPriceDescription = [
  '30 scripts per month',
  'Unlimited free admin users',
  'Send script via SMS and email',
  'Self-managed billing for multiple users',
  'Real Time Prescription Monitoring(RTPM)',
]

const unlimitedPriceDescription = [
  'Unlimited scripts per month',
  'Unlimited free admin users',
  'Send script via SMS and email',
  'Self-managed billing for multiple users',
  'Real Time Prescription Monitoring(RTPM)',
]

const enterprisePriceDescription = [
  'Single Sign On (SSO)',
  'Priority Customer Support',
  'API Integrations *',
]

const FreeTrialNotification = () => (
  <Text fontSize="xs" textAlign="left" width="100%">
    No credit card needed for free trial
  </Text>
)

type PricePlan = 'STANDARD' | 'UNLIMITED'

const PricePlanPage = () => {
  const toast = useToast()
  const dispatch = useAppDispatch()
  const [disabled, setDisabled] = useState(false)
  const [standardSeats, setStandardSeats] = useState(1)
  const [unlimitedSeats, setUnlimitedSeats] = useState(1)
  const organizationId = useAppSelector((state: RootState) => state.organization?.data?.id)
  const subscription = useAppSelector((state: RootState) => state.billing?.subscription.data)
  const admin = useAppSelector((state) => state.organizationAdmin.profile)
  const isMultipleSeatsSupport = location.search.includes('multiple_seats')
  const GAContextData = useGAContextData()
  const [selectedPricePlan, setSelectedPricePlan] = useState<PricePlan | null>(null)
  const { error } = useAppSelector((state: RootState) => state.billing?.subscription)

  useEffect(() => {
    if (!subscription && organizationId) {
      dispatch(BillingClient.getSubscription({ organizationId }))
    }
  }, [organizationId, dispatch])

  useEffect(() => {
    setDisabled(!admin?.isOwner || !!subscription)
  }, [subscription, admin])

  const { trigger: triggerCreateCheckoutSession, loading } = useApi(
    BillingClient.createCheckoutSession,
    {
      onSuccess: (checkoutSessionUrl: string) => {
        window.location.href = checkoutSessionUrl
      },
      onFailure: () => {
        toast({
          title: 'Create subscription failed',
          description:
            'You may already have a valid subscription or do not have permission to do it. Please contact your organisation owner to buy/update the plan.',
          status: 'error',
          isClosable: true,
          duration: 3000,
        })
        setDisabled(true)
      },
    }
  )

  const getStarted = (pricePlan: PricePlan, seats: number) => {
    return () => {
      setSelectedPricePlan(pricePlan)
      if (seats === 0) {
        toast({
          title: 'You have to choose at least 1 seat',
          status: 'warning',
          isClosable: true,
          duration: 3000,
        })
        return
      }
      GoogleAnalyticsTagManager.sendGetStartedPaymentEvent({ ...GAContextData, pricePlan })
      if (organizationId) {
        triggerCreateCheckoutSession({ pricePlan, organizationId, seats: seats || 1 })
      }
    }
  }

  const PricePlanButton = ({
    text,
    onClick,
    isLoading,
  }: {
    text: React.ReactNode
    onClick?: () => void
    isLoading?: boolean
  }) => (
    <Button
      bgColor="customBlue.100"
      color="white"
      disabled={isLoading || disabled}
      isLoading={isLoading}
      onClick={onClick}
      size="lg"
      width="100%"
    >
      {text}
    </Button>
  )

  const isTrialAvailable = error?.code === BillErrorCode.NO_SUBSCRIPTION

  return (
    <>
      <VStack overflow="auto" p={['16px', '24px']} minHeight="100%" spacing={[8, 8, 28]}>
        <TitleWithLogo title={'Select Plan'} />
        <SimpleGrid
          width={['90%', '80%', '100%', '80%', '70%']}
          columns={[1, 1, 3]}
          spacing={8}
          alignItems={'stretch'}
          mt={'24 !important'}
        >
          {/* STANDARD Card */}
          <Card>
            <VStack spacing={6} height="100%" p={8} alignItems="flex-start">
              <VStack spacing={2} alignItems="flex-start" paddingBottom={6}>
                <Heading size={'md'} as={'h2'} textAlign="left">
                  STANDARD
                </Heading>
                <HStack spacing={1} alignItems="flex-end">
                  <Text fontSize="4xl" as="b" fontFamily={'system-ui'} lineHeight="1">
                    $35
                  </Text>
                  <Text color="gray.600">per user per month</Text>
                </HStack>
              </VStack>
              <List spacing={4} flex="1">
                {standardPriceDescription.map((description) => (
                  <ListItem display="flex" alignItems="flex-start" key={description}>
                    <ListIcon as={CheckIcon} color="blue.500" boxSize={5} marginTop={0.5} />
                    <Text>{description}</Text>
                  </ListItem>
                ))}
              </List>
              <FreeTrialNotification />
              <VStack spacing={4} width="100%">
                {isMultipleSeatsSupport && (
                  <VStack spacing={2} width="100%">
                    <Text fontSize="sm" color="gray.600" fontWeight="medium">
                      Number of seats
                    </Text>
                    <NumberInput
                      defaultValue={1}
                      min={0}
                      max={999}
                      width="140px"
                      size="lg"
                      value={standardSeats}
                      onChange={(valueString) => {
                        const value = valueString ? valueString : '0'
                        setUnlimitedSeats(0)
                        setStandardSeats(parseInt(value))
                      }}
                    >
                      <NumberInputField textAlign="center" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </VStack>
                )}
                <PricePlanButton
                  key="standard"
                  text={isTrialAvailable ? 'Try 30 days free' : 'Get Started'}
                  onClick={getStarted('STANDARD', standardSeats)}
                  isLoading={loading && selectedPricePlan === 'STANDARD'}
                />
              </VStack>
            </VStack>
          </Card>

          {/* UNLIMITED Card */}
          <Card position="relative" boxShadow="xl">
            <Text
              as={'b'}
              color="white"
              fontSize={'13px'}
              bgColor={'customBlue.100'}
              textAlign={'center'}
              letterSpacing="wider"
              position="absolute"
              top={'-4'}
              left={'50%'}
              transform="translate(-50%, -1%)"
              padding={1.5}
              borderRadius={12}
            >
              MOST POPULAR
            </Text>
            <VStack
              spacing={2}
              alignItems="flex-start"
              padding={8}
              bg="customBlue.100"
              color="white"
              borderTopRadius={'md'}
              paddingBottom={6}
            >
              <Heading size={'md'} as={'h2'} textAlign="left">
                UNLIMITED
              </Heading>
              <HStack spacing={1} alignItems="flex-end">
                <Text fontSize="4xl" as="b" fontFamily={'system-ui'} lineHeight="1">
                  $65
                </Text>
                <Text>per user per month</Text>
              </HStack>
            </VStack>
            <VStack spacing={6} height="100%" p={8} paddingTop={6}>
              <List spacing={4} flex="1" alignSelf="flex-start">
                {unlimitedPriceDescription.map((description) => (
                  <ListItem display="flex" alignItems="flex-start" key={description}>
                    <ListIcon as={CheckIcon} color="blue.500" boxSize={5} marginTop={0.5} />
                    <Text>{description}</Text>
                  </ListItem>
                ))}
              </List>
              <FreeTrialNotification />

              <VStack spacing={4} width="100%">
                {isMultipleSeatsSupport && (
                  <VStack spacing={2} width="100%">
                    <Text fontSize="sm" fontWeight="medium">
                      Number of seats
                    </Text>
                    <NumberInput
                      defaultValue={1}
                      min={0}
                      max={999}
                      width="140px"
                      size="lg"
                      value={unlimitedSeats}
                      onChange={(valueString) => {
                        const value = valueString ? valueString : '0'
                        setStandardSeats(0)
                        setUnlimitedSeats(parseInt(value))
                      }}
                    >
                      <NumberInputField textAlign="center" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </VStack>
                )}
                <PricePlanButton
                  key="unlimited"
                  text={isTrialAvailable ? 'Try 30 days free' : 'Get Started'}
                  onClick={getStarted('UNLIMITED', unlimitedSeats)}
                  isLoading={loading && selectedPricePlan === 'UNLIMITED'}
                />
              </VStack>
            </VStack>
          </Card>

          {/* GROUP Card */}
          <Card>
            <VStack spacing={6} height="100%" p={8} alignItems="flex-start">
              <VStack spacing={2} alignItems="flex-start" paddingBottom={8} lineHeight={2}>
                <Heading size={'md'} as={'h2'} textAlign="left">
                  ENTERPRISE
                </Heading>
                <Text fontSize="xl" as="b" fontFamily={'system-ui'} lineHeight="1.2">
                  Contact us
                </Text>
              </VStack>
              <List spacing={4} flex="1">
                <ListItem display="flex" alignItems="flex-start" key="enterprise-1">
                  <Text>For organisations intending to purchase 50+ prescribing seats</Text>
                </ListItem>
                <ListItem display="flex" alignItems="flex-start" key="enterprise-2">
                  <Text>Includes all Unlimited Tier features, plus:</Text>
                </ListItem>
                {enterprisePriceDescription.map((description) => (
                  <ListItem display="flex" alignItems="flex-start" key={description}>
                    <ListIcon as={CheckIcon} color="blue.500" boxSize={5} marginTop={0.5} />
                    <Text>{description}</Text>
                  </ListItem>
                ))}
              </List>

              <Text fontSize="xs">
                * Please complete the{' '}
                <Link
                  color="customBlue.100"
                  target="_blank"
                  href="https://forms.office.com/r/eeY9504g7x"
                >
                  RxPad Integration Questionnaire
                </Link>{' '}
                to help us better assess your integration needs.
              </Text>

              <Button
                bgColor="customBlue.100"
                color="white"
                as={Link}
                size="lg"
                width="100%"
                href="mailto:support@rxpad.com.au"
                target="_blank"
                isExternal
              >
                Talk to us
              </Button>
            </VStack>
          </Card>
        </SimpleGrid>

        <Card width={['90%', '80%', '100%', '80%', '70%']} bgColor="gray.50" mt={'12 !important'}>
          <CardBody>
            <Heading size={'md'} as={'h2'} textAlign="left">
              Want to get started sooner?
            </Heading>
            <Text mt={4}>
              Choose one of our Standard or Unlimited subscription plans to start your 30-day free
              trial (no credit card needed) while we process your enterprise enquiry.
            </Text>
          </CardBody>
        </Card>
      </VStack>
    </>
  )
}
export default PricePlanPage
